/*
 * Sierra Sydney - cart drawer interactions (redesign)
 *
 * - Quantity stepper and remove for compact rows. A row can stand for several
 *   cart lines of the same variant; updates go to /cart/update.js with every
 *   line key so the duplicates collapse into one line.
 * - Size dropdown: swaps the variant in place (add new, remove old).
 * - Duplicate lines of the same variant are merged once, automatically.
 * - "Complete the look": loads related products, adds them in one tap and
 *   scrolls with arrows (desktop) or swipe (touch).
 *
 * Everything is delegated from the document, so it keeps working after the
 * drawer is re-rendered through the Section Rendering API.
 */
(function () {
  if (window.__ssCartDrawerUI) return;
  window.__ssCartDrawerUI = true;

  var SECTIONS = ['cart-counter', 'cart-drawer'];
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var busy = false;
  var recosCache = {};
  var mergeTried = {};

  /* ---------- helpers ---------- */

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function body() {
    return document.getElementById('CartDrawer-Body');
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function moneyFormat() {
    var el = document.querySelector('[data-ss-money-format]');
    return (el && el.getAttribute('data-ss-money-format')) || '${{amount}}';
  }

  function formatMoney(cents, trimZeros) {
    cents = Number(cents) || 0;
    function withDelimiters(number, precision, thousands, decimal) {
      var parts = (number / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      if (trimZeros && /^[.,]0+$/.test(cents)) cents = '';
      return whole + cents;
    }
    return moneyFormat().replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
  }

  function setLoading(on) {
    busy = on;
    var items = document.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-loading', on);
  }

  function postJSON(path, payload) {
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (json) {
          if (!res.ok || json.status) {
            var fallback = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
            throw new Error(json.description || json.message || fallback);
          }
          return json;
        });
    });
  }

  function withSections(payload) {
    payload.sections = SECTIONS;
    payload.sections_url = window.location.pathname;
    return payload;
  }

  // Swap in the fresh header, body and bag counter from the section response.
  function render(state) {
    if (!state || !state.sections) return;
    var parts = [
      { section: 'cart-drawer', selector: '#CartDrawer-Header' },
      { section: 'cart-drawer', selector: '#CartDrawer-Body' },
      { section: 'cart-counter', selector: '#cart-counter', source: '#shopify-section-cart-counter' },
    ];
    parts.forEach(function (part) {
      var html = state.sections[part.section];
      var current = document.querySelector(part.selector);
      if (!html || !current) return;
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(part.source || part.selector);
      if (fresh) current.innerHTML = fresh.innerHTML;
    });
    if (typeof window.updateFreeShipping === 'function') window.updateFreeShipping();
  }

  function showLineError(line, message) {
    var el = line && line.querySelector('[data-ss-line-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  // After a re-render, put focus back on the same control of the same row.
  function restoreFocus(variantId, selector) {
    if (!variantId) return;
    var row = document.querySelector('[data-ss-line][data-variant="' + variantId + '"]');
    var target = row && row.querySelector(selector);
    if (target) target.focus();
  }

  /* ---------- lines: quantity, remove, merge ---------- */

  function setLineQuantity(line, qty) {
    var keys = (line.getAttribute('data-keys') || '').split(',').filter(Boolean);
    if (!keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (key, i) {
      if (!(key in updates)) updates[key] = i === 0 ? qty : 0;
    });
    setLoading(true);
    showLineError(line, '');
    return postJSON('cart/update.js', withSections({ updates: updates }))
      .then(render)
      .catch(function (err) {
        var val = line.querySelector('[data-ss-qty-val]');
        if (val) val.textContent = line.getAttribute('data-qty');
        delete line.dataset.pending;
        showLineError(line, err.message);
      })
      .then(function () {
        setLoading(false);
      });
  }

  var qtyTimers = new WeakMap();

  function onQtyClick(btn) {
    var line = btn.closest('[data-ss-line]');
    if (!line) return;
    var current = parseInt(line.dataset.pending || line.getAttribute('data-qty'), 10) || 0;
    var next = Math.max(0, current + parseInt(btn.getAttribute('data-ss-qty'), 10));
    line.dataset.pending = String(next);
    var val = line.querySelector('[data-ss-qty-val]');
    if (val) val.textContent = String(next);

    clearTimeout(qtyTimers.get(line));
    qtyTimers.set(
      line,
      setTimeout(function () {
        var variantId = line.getAttribute('data-variant');
        var focusSel = '[data-ss-qty="' + btn.getAttribute('data-ss-qty') + '"]';
        setLineQuantity(line, next).then(function () {
          restoreFocus(variantId, focusSel);
        });
      }, 350)
    );
  }

  function onRemoveClick(btn) {
    var line = btn.closest('[data-ss-line]');
    if (line) setLineQuantity(line, 0);
  }

  // Same variant on two or more lines: fold them into the first line, once.
  function mergeDuplicates() {
    if (busy) return;
    var root = body();
    if (!root) return;
    var lines = root.querySelectorAll('[data-ss-line]');
    for (var i = 0; i < lines.length; i++) {
      var keys = (lines[i].getAttribute('data-keys') || '').split(',').filter(Boolean);
      var unique = keys.filter(function (k, idx) {
        return keys.indexOf(k) === idx;
      });
      if (unique.length < 2) continue;
      var signature = unique.slice().sort().join('|');
      if (mergeTried[signature]) continue;
      mergeTried[signature] = true;
      setLineQuantity(lines[i], parseInt(lines[i].getAttribute('data-qty'), 10) || 1);
      return;
    }
  }

  /* ---------- size dropdown ---------- */

  function syncSizeLabel(select) {
    var label = select.closest('.ss-cd-select');
    var value = label && label.querySelector('[data-ss-size-value]');
    var option = select.options[select.selectedIndex];
    if (value && option) value.textContent = option.textContent.replace(/\s*\(sold out\)\s*$/, '').trim();
  }

  function onSizeChange(select) {
    var line = select.closest('[data-ss-line]');
    if (!line) return;
    var oldVariant = line.getAttribute('data-variant');
    var newVariant = select.value;
    if (!newVariant || newVariant === oldVariant) return;
    syncSizeLabel(select);

    var keys = (line.getAttribute('data-keys') || '').split(',').filter(Boolean);
    var qty = parseInt(line.getAttribute('data-qty'), 10) || 1;
    // Liquid prints empty line properties as [] (an array). /cart/add.js only
    // accepts a hash, so anything that is not a non-empty object is dropped.
    var props = null;
    try {
      props = JSON.parse(line.getAttribute('data-props') || 'null');
    } catch (e) {}
    var item = { id: parseInt(newVariant, 10), quantity: qty };
    if (props && typeof props === 'object' && !Array.isArray(props) && Object.keys(props).length) {
      item.properties = props;
    }
    var plan = line.getAttribute('data-plan');
    if (plan) item.selling_plan = parseInt(plan, 10);

    setLoading(true);
    showLineError(line, '');
    postJSON('cart/add.js', { items: [item] })
      .then(function () {
        var updates = {};
        keys.forEach(function (k) {
          updates[k] = 0;
        });
        return postJSON('cart/update.js', withSections({ updates: updates }));
      })
      .then(function (state) {
        render(state);
        restoreFocus(newVariant, '[data-ss-size]');
      })
      .catch(function (err) {
        select.value = oldVariant;
        syncSizeLabel(select);
        showLineError(line, err.message);
      })
      .then(function () {
        setLoading(false);
      });
  }

  /* ---------- Complete the look ---------- */

  function addToBag(variantId, button) {
    if (!variantId) return;
    var card = button && button.closest('.ss-cd-reco');
    var addBtn = card && card.querySelector('[data-ss-add]');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
    }
    postJSON('cart/add.js', withSections({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }))
      .then(function (state) {
        render(state);
        scrollDrawerToTop();
      })
      .catch(function (err) {
        if (!addBtn) return;
        addBtn.textContent = /sold out|not enough|unavailable/i.test(err.message) ? 'Sold out' : 'Try again';
        setTimeout(function () {
          addBtn.disabled = false;
          addBtn.textContent = '+ Add';
        }, 2200);
      });
  }

  // After a "Complete the look" add, bring the bag lines back into view so the
  // customer can see the new item. The drawer panel is the scroll container.
  function scrollDrawerToTop() {
    var panel = document.querySelector('.ss-cd.cart-drawer__inner');
    if (!panel) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof panel.scrollTo === 'function') {
      panel.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    } else {
      panel.scrollTop = 0;
    }
  }

  function onAddClick(btn) {
    var variant = btn.getAttribute('data-variant');
    if (variant) {
      addToBag(variant, btn);
      return;
    }
    var card = btn.closest('.ss-cd-reco');
    var sizes = card && card.querySelector('[data-ss-sizes]');
    if (!sizes) return;
    var opening = sizes.hidden;
    document.querySelectorAll('.ss-cd [data-ss-sizes]').forEach(function (el) {
      el.hidden = true;
    });
    sizes.hidden = !opening;
    btn.setAttribute('aria-expanded', String(opening));
    if (opening) {
      var first = sizes.querySelector('button');
      if (first) first.focus();
    }
  }

  function recoCard(product) {
    var available = (product.variants || []).filter(function (v) {
      return v.available;
    });
    var image = product.featured_image || (product.images && product.images[0]) || '';
    if (image && typeof image === 'object') image = image.src || '';
    if (image) image += (image.indexOf('?') > -1 ? '&' : '?') + 'width=300';
    var title = escapeHtml(product.title);
    var url = escapeHtml(product.url);

    var html =
      '<div class="ss-cd-reco">' +
      '<a class="ss-cd-reco__media" href="' + url + '" tabindex="-1" aria-hidden="true">' +
      (image ? '<img src="' + escapeHtml(image) + '" width="150" height="150" alt="" loading="lazy">' : '') +
      '</a>' +
      '<a class="ss-cd-reco__title" href="' + url + '">' + title + '</a>' +
      '<div class="ss-cd-reco__row">' +
      '<span class="ss-cd-reco__price">' + escapeHtml(formatMoney(product.price, true)) + '</span>' +
      '<button type="button" class="ss-cd-reco__add" data-ss-add' +
      (available.length === 1 ? ' data-variant="' + available[0].id + '"' : '') +
      ' aria-label="Add ' + title + ' to bag">+ Add</button>' +
      '</div>';

    if (available.length > 1) {
      html += '<div class="ss-cd-reco__sizes" data-ss-sizes hidden>';
      available.forEach(function (v) {
        html +=
          '<button type="button" class="ss-cd-reco__size" data-ss-add-variant="' + v.id + '" aria-label="Add ' + title + ', ' + escapeHtml(v.title) + '">' +
          escapeHtml(v.title) +
          '</button>';
      });
      html += '</div>';
    }
    return html + '</div>';
  }

  function scrollRecos(btn, dir) {
    var wrap = btn.closest('[data-ss-recos]');
    var track = wrap && wrap.querySelector('[data-ss-recos-track]');
    if (!track) return;
    var card = track.querySelector('.ss-cd-reco');
    var step = card ? card.getBoundingClientRect().width + 8 : track.clientWidth * 0.8;
    var perView = Math.max(1, Math.floor((track.clientWidth + 8) / step));
    track.scrollBy({ left: dir * step * perView, behavior: 'smooth' });
  }

  function updateRecosArrows() {
    document.querySelectorAll('.ss-cd [data-ss-recos]').forEach(function (wrap) {
      var track = wrap.querySelector('[data-ss-recos-track]');
      var arrows = wrap.querySelector('[data-ss-recos-arrows]');
      if (!track || !arrows) return;
      if (!track.dataset.ssBound) {
        track.dataset.ssBound = '1';
        track.addEventListener('scroll', updateRecosArrows, { passive: true });
      }
      var max = track.scrollWidth - track.clientWidth;
      arrows.hidden = max <= 2;
      var prev = arrows.querySelector('[data-ss-recos-prev]');
      var next = arrows.querySelector('[data-ss-recos-next]');
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    });
  }

  window.addEventListener('resize', function () {
    updateRecosArrows();
  });

  function loadRelated() {
    var wrap = document.querySelector('.ss-cd [data-ss-recos][data-mode="related"]');
    if (!wrap || wrap.dataset.loaded) return;
    wrap.dataset.loaded = '1';
    var url = wrap.getAttribute('data-url');
    var exclude = wrap.getAttribute('data-exclude') || ',';
    var limit = parseInt(wrap.getAttribute('data-limit'), 10) || 6;
    var track = wrap.querySelector('[data-ss-recos-track]');

    var request = recosCache[url] || (recosCache[url] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        delete recosCache[url];
        return [];
      }));

    request.then(function (products) {
      if (!wrap.isConnected) return;
      var picks = products
        .filter(function (p) {
          return p.available && exclude.indexOf(',' + p.id + ',') === -1;
        })
        .slice(0, limit);
      if (!picks.length) return;
      track.innerHTML = picks.map(recoCard).join('');
      wrap.hidden = false;
    });
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest || !t.closest('.ss-cd')) return;
    var el;
    if ((el = t.closest('[data-ss-qty]'))) return onQtyClick(el);
    if ((el = t.closest('[data-ss-remove]'))) return onRemoveClick(el);
    if ((el = t.closest('[data-ss-add-variant]'))) return addToBag(el.getAttribute('data-ss-add-variant'), el);
    if ((el = t.closest('[data-ss-add]'))) return onAddClick(el);
    if ((el = t.closest('[data-ss-recos-prev]'))) return scrollRecos(el, -1);
    if ((el = t.closest('[data-ss-recos-next]'))) return scrollRecos(el, 1);
  });

  document.addEventListener('change', function (event) {
    var t = event.target;
    if (t && t.matches && t.matches('.ss-cd [data-ss-size]')) onSizeChange(t);
  });

  // Escape closes an open size picker first; the drawer closes on keyup, so
  // that keyup is swallowed too.
  var swallowEscapeUp = false;
  document.addEventListener('keyup', function (event) {
    if (event.key !== 'Escape' || !swallowEscapeUp) return;
    swallowEscapeUp = false;
    event.stopPropagation();
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('.ss-cd [data-ss-sizes]:not([hidden])');
    if (!open) return;
    event.stopPropagation();
    swallowEscapeUp = true;
    open.hidden = true;
    var add = open.closest('.ss-cd-reco').querySelector('[data-ss-add]');
    if (add) {
      add.setAttribute('aria-expanded', 'false');
      add.focus();
    }
  }, true);

  var refreshQueued = false;
  function refresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    setTimeout(function () {
      refreshQueued = false;
      loadRelated();
      updateRecosArrows();
      mergeDuplicates();
    }, 60);
  }

  function init() {
    refresh();
    var drawer = drawerEl();
    if (!drawer) return;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t.nodeType === 1 && t.closest && t.closest('[data-ss-recos-track]')) {
          updateRecosArrows();
          continue;
        }
        refresh();
        return;
      }
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
